import React, {useEffect, useRef, useState} from "react";
import { Grid, Box, Typography, IconButton, Button, CircularProgress } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import AddShoppingCartRoundedIcon from "@mui/icons-material/AddShoppingCartRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import RestaurantMenuRoundedIcon from "@mui/icons-material/RestaurantMenuRounded";
import RoomServiceRoundedIcon from "@mui/icons-material/RoomServiceRounded";
import AssessmentRoundedIcon from "@mui/icons-material/AssessmentRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import KitchenRoundedIcon from "@mui/icons-material/KitchenRounded";
import ChecklistRoundedIcon from "@mui/icons-material/ChecklistRounded";
import DeleteSweepRoundedIcon from "@mui/icons-material/DeleteSweepRounded";
import ForumRoundedIcon from "@mui/icons-material/ForumRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import Sidebar from "./sideBar";
import SystemLoginPage from "../SystemLoginPage/SystemLoginPage";
import useAuthToken from "./utils/authToken";
import useCurrentTime from "./utils/currentTime";
import { useGlobalOrder } from "../../utils/GlobalStateContext";

import {getOrders, removeOrder} from "../../utils/ordersControlar";
import SopReminder from "../SopChecklist/SopReminder";
import ReservationReminder from "../Reservations/ReservationReminder";
import TrashReminder from "../Reminders/TrashReminder";
import PausedProductsList from "../PausedProducts/PausedProductsList";
import { collectPausedProducts, formatBadgeCount } from "../PausedProducts/pausedProducts";
import TodayReservationsCard from "../Reservations/TodayReservationsCard";
import { TodayReservationsProvider, useTodayReservations } from "../Reservations/TodayReservationsProvider";
import KioskShell from "../../kiosk/KioskShell";
import {COLORS} from "../../brand";
import {isNativeApp} from "../../utils/nativeBridge";

function MainMenuContent() {
  const { dueCount: reservationDueCount } = useTodayReservations();
  const { systemData, setSystemData, setGlobalOrder , currentTab , setCurrentTab , todayOrders, setTodayOrders , setUrlType , urlType , chatUnread , pendingMakeCount , refreshProductData } = useGlobalOrder();
  const navigate = useNavigate(); // 用來導航至其他頁面
  const location = useLocation();

  // 通知 toast（在 Router 外層）點擊後透過自訂事件導頁
  useEffect(() => {
    const handlePosNavigate = (event) => {
      if (event.detail) {
        navigate(event.detail);
      }
    };
    window.addEventListener("pos-navigate", handlePosNavigate);
    return () => window.removeEventListener("pos-navigate", handlePosNavigate);
  }, [navigate]);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const sidebarCollapseTimer = useRef(null);

  useEffect(() => {
    if (sidebarCollapseTimer.current) {
      clearTimeout(sidebarCollapseTimer.current);
      sidebarCollapseTimer.current = null;
    }
    if (!sidebarCollapsed) {
      sidebarCollapseTimer.current = setTimeout(() => {
        setSidebarCollapsed(true);
      }, 60 * 1000);
    }
    return () => {
      if (sidebarCollapseTimer.current) {
        clearTimeout(sidebarCollapseTimer.current);
        sidebarCollapseTimer.current = null;
      }
    };
  }, [location.pathname, sidebarCollapsed]);

  const currentTime = useCurrentTime(); // 使用自定義的時間 hook
  const { systemLogin, loading, setSystemLogin , checkAuthentication } = useAuthToken(
    setTodayOrders,
    setSystemData
  ); // 使用自定義的 token hook

  // POS2(炒台)/POS3(出餐台) 用不到訊息中心，選單與首頁總覽都不顯示
  // 轉盤活動已移入營業資訊頁籤（Income.jsx），不再是獨立選單
  const hiddenMenuIds = [2, 3].includes(Number(systemData?.position_id)) ? [13] : [];
  const menuItems = [
    { id: 0 , label: "首頁", path: "/" },
    { id: 1 , label: "建立點餐", path: "/order" },
    { id: 2 ,label: "訂單記錄", path: "/order-history" },
    { id: 14 ,label: "預約管理", path: "/reservations" },
    { id: 13 ,label: "訊息中心", path: "/chat" },
    { id: 5 ,label: "出餐狀況", path: "/dining-out" },
    { id: 3 ,label: "餐點管理", path: "/inventory" },
    { id: 9 ,label: "作業檢核", path: "/sop" },
    { id: 15 ,label: "報廢登記", path: "/waste" },
    // 盤點叫貨已移入「營業資訊 > 盤點叫貨」頁籤（Income.jsx），不再是獨立選單
    // 進貨管理已整合進「營業資訊 > 現金盤點」頁籤，從盤點頁的「今日進貨」進入

    // { id: 8 ,label: "備置管理", path: "/prep-management" },
    { id: 6 ,label: "營業資訊", path: "/income" },
    { id: 7 ,label: "登出", path: "/logout" },
  ].filter((item) => !hiddenMenuIds.includes(item.id));

  useEffect(  () => {
    if (systemLogin && systemData?.client_mode !== "kiosk" && location.pathname === "/") {
      setTodayOrders(null);
      getOrders({now:1}).then((response)=>{
        setTodayOrders(response);
      });
      // 回首頁順便重抓商品資料，讓暫停販售區塊與後續點餐都是最新狀態
      refreshProductData();
    }
  }, [systemLogin, systemData?.client_mode, location.pathname]);

  const refreshTodayOrders = () => {
    setTodayOrders(null);
    getOrders({now:1}).then((response)=>{
      setTodayOrders(response);
    });
  };

  const handleDeleteBlankOrder = async (event, orderId) => {
    event.stopPropagation();
    const result = await removeOrder(orderId, {});
    if (result?.error === 0) {
      refreshTodayOrders();
    }
  };

  const visibleMenuItems = menuItems.filter((item) => item.id !== 0 && item.id !== 7);
  const pendingOrders = todayOrders?.length || 0;
  const pausedProducts = collectPausedProducts(systemData);
  // 選單紅字：訊息未讀、未出餐單數、暫停販售品項數
  const menuBadges = { 13: chatUnread, 5: pendingMakeCount, 3: pausedProducts.length };
  const menuIcons = {
    1: <AddShoppingCartRoundedIcon />,
    2: <ReceiptLongRoundedIcon />,
    3: <RestaurantMenuRoundedIcon />,
    4: <Inventory2RoundedIcon />,
    5: <RoomServiceRoundedIcon />,
    6: <AssessmentRoundedIcon />,
    8: <KitchenRoundedIcon />,
    9: <ChecklistRoundedIcon />,
    13: <ForumRoundedIcon />,
    14: <CalendarMonthRoundedIcon />,
    15: <DeleteSweepRoundedIcon />,
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          backgroundColor: "#FFFBEB",
        }}
      >
        <Box
          sx={{
            width: 260,
            minHeight: 220,
            borderRadius: "26px",
            backgroundColor: "#FFF",
            border: "1px solid rgba(32,33,36,0.08)",
            boxShadow: "0 24px 70px rgba(31,35,40,0.12)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
          }}
        >
          <Box
            sx={{
              width: 58,
              height: 58,
              borderRadius: "18px",
              backgroundColor: "#FFF1C2",
              color: "#1C2D42",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <StorefrontRoundedIcon sx={{ fontSize: 32 }} />
          </Box>
          <CircularProgress size={28} sx={{ color: "#1C2D42" }} />
          <Typography sx={{ color: "#536073", fontSize: 16, fontWeight: 800 }}>
            系統載入中
          </Typography>
        </Box>
      </Box>
    );
  }

  if (systemData?.client_mode === "kiosk") {
    if (location.pathname === "/") {
      return <Navigate to="/kiosk/welcome" replace/>;
    }
    return <KioskShell systemData={systemData}/>;
  }

  return systemData ? (
    <Grid
      container
      sx={{
        display: "flex",
        flexWrap: "nowrap",
        width: "100%",
        height: "100vh",
        overflow: "hidden",
        backgroundColor: "#FFFBEB",
      }}
    >
      <SopReminder />
      <ReservationReminder />
      <TrashReminder />
      {/* App 內由原生主選單負責導覽，不顯示網頁側欄 */}
      {!isNativeApp() && (
        <Grid
          item
          className="h-full flex"
          sx={{
            width: sidebarCollapsed ? "88px" : "224px",
            flex: `0 0 ${sidebarCollapsed ? "88px" : "224px"}`,
            flexDirection: "column",
            justifyContent: "space-between",
            transition: "width 220ms cubic-bezier(0.22, 1, 0.36, 1), flex-basis 220ms cubic-bezier(0.22, 1, 0.36, 1)",
            background: COLORS.ink,
            borderRight: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          <Sidebar
            systemData={systemData}
            setSystemLogin={setSystemLogin}
            menuItems={menuItems}
            collapsed={sidebarCollapsed}
            onToggleCollapsed={() => setSidebarCollapsed((value) => !value)}
            urlType={urlType}
            setUrlType={setUrlType}
            setTodayOrders={setTodayOrders}
            setCurrentTab={setCurrentTab}
          />
        </Grid>
      )}
      <Grid
        item
        sx={{
          flex: 1,
          minWidth: 0,
          height: "100vh",
          overflow: "hidden",
          backgroundColor: "#FFFBEB",
        }}
      >
        <Box sx={{ height: "100%", width: "100%" }}>
          {/* 右側頁面顯示 */}
          {window.location.pathname === "/" ? (
            <Box
              sx={{
                height: "100vh",
                width: "100%",
                overflow: "auto",
                p: 3,
              }}
              className="hide-scrollbar"
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1fr) 440px" },
                  gap: 3,
                  minHeight: "100%",
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 2,
                      mb: 3,
                    }}
                  >
                    <Box>
                      <Typography
                        sx={{
                          color: "#202124",
                          fontSize: 28,
                          lineHeight: "34px",
                          fontWeight: 800,
                        }}
                      >
                        {systemData.store_name || "餐飲管理系統"}
                      </Typography>
                      <Typography sx={{ color: "#536073", fontSize: 15, mt: 0.5 }}>
                        {currentTime} · 今日暫存訂單 {pendingOrders} 筆
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        width: 96,
                        height: 96,
                        borderRadius: "20px",
                        backgroundColor: "#FFF",
                        boxShadow: "0 10px 30px rgba(31, 35, 40, 0.08)",
                        border: "1px solid rgba(32,33,36,0.08)",
                        p: 1.5,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <img
                        src={systemData.store_image}
                        alt="logo"
                        className="w-full h-full object-contain"
                      />
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
                      gap: 2,
                    }}
                  >
                    {visibleMenuItems.map((item) => (
                      <Button
                        key={item.id}
                        onClick={() => {
                          navigate(item.path);
                          setCurrentTab(item.id);
                        }}
                        sx={{
                          height: 128,
                          borderRadius: "18px",
                          p: 2,
                          backgroundColor: "#FFF",
                          color: "#202124",
                          border: "1px solid rgba(32,33,36,0.08)",
                          boxShadow: "0 10px 30px rgba(31, 35, 40, 0.08)",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                          justifyContent: "space-between",
                          textAlign: "left",
                          "&:hover": {
                            backgroundColor: "#EDF0F4",
                            borderColor: "rgba(28,45,66,0.24)",
                            transform: "translateY(-1px)",
                            boxShadow: "0 14px 36px rgba(31, 35, 40, 0.12)",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            width: 44,
                            height: 44,
                            borderRadius: "14px",
                            backgroundColor: "#FFF1C2",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#1C2D42",
                            position: "relative",
                            "& svg": {
                              fontSize: 28,
                            },
                          }}
                        >
                          {menuIcons[item.id] || item.label.slice(0, 1)}
                          {item.id === 14 && reservationDueCount > 0 ? (
                            <Box
                              sx={{
                                position: "absolute",
                                top: -6,
                                right: -8,
                                minWidth: 20,
                                height: 20,
                                px: 0.5,
                                borderRadius: "999px",
                                backgroundColor: "#F57C00",
                                color: "#FFF",
                                fontSize: 12,
                                fontWeight: 800,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              {reservationDueCount > 99 ? "99+" : reservationDueCount}
                            </Box>
                          ) : null}
                          {menuBadges[item.id] > 0 ? (
                            <Box
                              sx={{
                                position: "absolute",
                                top: -6,
                                right: -8,
                                minWidth: 20,
                                height: 20,
                                px: 0.5,
                                borderRadius: "999px",
                                backgroundColor: "#D32F2F",
                                color: "#FFF",
                                fontSize: 12,
                                fontWeight: 800,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              {formatBadgeCount(menuBadges[item.id])}
                            </Box>
                          ) : null}
                        </Box>
                        <Box>
                          <Typography sx={{ fontSize: 20, fontWeight: 800, lineHeight: "24px" }}>
                            {item.label}
                          </Typography>
                          <Typography sx={{ color: "#536073", fontSize: 13, mt: 0.5 }}>
                            點選進入
                          </Typography>
                        </Box>
                      </Button>
                    ))}
                  </Box>

                  {/* 今日預約：待處理筆數與最近三筆，時間快到的會標色，資料與全域提醒共用 */}
                  <TodayReservationsCard onSelectTab={setCurrentTab} />

                  {/* 暫停販售品項總覽：讓員工不用進餐點管理就能一眼看到目前不能賣什麼 */}
                  <Box
                    sx={{
                      mt: 3,
                      backgroundColor: "#FFF",
                      borderRadius: "22px",
                      border: "1px solid rgba(32,33,36,0.08)",
                      boxShadow: "0 10px 30px rgba(31, 35, 40, 0.08)",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        px: 2.5,
                        py: 2,
                        borderBottom: "1px solid rgba(32,33,36,0.08)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box>
                        <Typography sx={{ color: "#202124", fontSize: 20, fontWeight: 800 }}>
                          暫停販售品項
                          {pausedProducts.length ? `（${pausedProducts.length}）` : ""}
                        </Typography>
                        <Typography sx={{ color: "#536073", fontSize: 13 }}>
                          含暫停與停售品項，狀態即時同步
                        </Typography>
                      </Box>
                      <Button
                        onClick={() => {
                          navigate("/inventory");
                          setCurrentTab(3);
                        }}
                        sx={{
                          color: "#1C2D42",
                          fontWeight: 800,
                          borderRadius: "999px",
                          px: 2,
                        }}
                      >
                        前往管理
                      </Button>
                    </Box>
                    <Box sx={{ p: 2 }}>
                      <PausedProductsList />
                    </Box>
                  </Box>
                </Box>

                <Box
                  sx={{
                    backgroundColor: "#FFF",
                    borderRadius: "22px",
                    border: "1px solid rgba(32,33,36,0.08)",
                    boxShadow: "0 10px 30px rgba(31, 35, 40, 0.08)",
                    minHeight: 0,
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box
                    sx={{
                      px: 2.5,
                      py: 2,
                      borderBottom: "1px solid rgba(32,33,36,0.08)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box>
                      <Typography sx={{ color: "#202124", fontSize: 20, fontWeight: 800 }}>
                        暫存訂單
                      </Typography>
                      <Typography sx={{ color: "#536073", fontSize: 13 }}>
                        可直接點選繼續處理
                      </Typography>
                    </Box>
                    <Button
                      onClick={refreshTodayOrders}
                      sx={{
                        color: "#1C2D42",
                        fontWeight: 800,
                        borderRadius: "999px",
                        px: 2,
                      }}
                    >
                      更新
                    </Button>
                  </Box>
                  <Grid
                      container
                      direction={"column"}
                      sx={{
                        gap: 1.5,
                        flex: 1,
                        overflow: "auto",
                        flexWrap: "nowrap",
                        p: 2,
                      }}
                      className="hide-scrollbar"
                  >
                    {todayOrders?.length
                        ? todayOrders.map((order) => {
                            const isActive = [0, 1, 2].includes(order.status);
                            const isUnpaid = order.pay_method === "未付款" || order.red_tag;
                            const secondaryText = "#536073";
                            const dividerText = "rgba(32,33,36,0.32)";
                            const statusColor = order.status_class || (isActive ? "#C2570A" : "#1C2D42");
                            const paymentColor = isUnpaid ? "#B30000" : secondaryText;
                            return (
                                <Box
                                    key={order.order_id}
                                    sx={{
                                      background: isUnpaid ? "#FDE7E7" : isActive ? "#FFF8E8" : "#FFF",
                                      borderRadius: "16px",
                                      position: "relative",
                                      border: "1px solid",
                                      borderColor: isUnpaid ? "#E8B4B4" : isActive ? "#F1D6A8" : "rgba(32,33,36,0.08)",
                                      borderLeft: "5px solid",
                                      borderLeftColor: isUnpaid ? "#C62828" : isActive ? "#F57C00" : "#D8D8D8",
                                      boxShadow: "0 6px 18px rgba(31, 35, 40, 0.06)",
                                      cursor: "pointer",
                                      overflow: "hidden",
                                      px: 2,
                                      py: 1.5,
                                      pr: order.is_blank ? 6 : 2,
                                      transition: "transform 120ms ease, box-shadow 120ms ease, background-color 120ms ease",
                                      "&:hover": {
                                        transform: "translateY(-1px)",
                                        boxShadow: "0 10px 24px rgba(31, 35, 40, 0.10)",
                                        backgroundColor: isUnpaid ? "#FBD9D9" : isActive ? "#FFF3D6" : "#FAFAFA",
                                      },
                                    }}
                                    onClick={() => {
                                      navigate("/order");
                                      setGlobalOrder(order);
                                    }}
                                >
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 0.75 }}>
                                    <Box
                                        sx={{
                                          flexShrink: 0,
                                          px: 1.25,
                                          py: 0.25,
                                          borderRadius: "999px",
                                          backgroundColor: "#FFF1C2",
                                          color: "#1C2D42",
                                          fontSize: 13,
                                          fontWeight: 800,
                                          lineHeight: "20px",
                                        }}
                                    >
                                      {order.method_text || "-"}
                                    </Box>
                                    {Boolean(order.is_kiosk) && (
                                        <Box
                                            sx={{
                                              flexShrink: 0,
                                              px: 1.25,
                                              py: 0.25,
                                              borderRadius: "999px",
                                              backgroundColor: "#E8F0FE",
                                              color: "#1A56A8",
                                              fontSize: 13,
                                              fontWeight: 800,
                                              lineHeight: "20px",
                                            }}
                                        >
                                          自助點餐
                                        </Box>
                                    )}
                                    <Typography
                                        sx={{
                                          flex: 1,
                                          minWidth: 0,
                                          color: "#202124",
                                          fontSize: 18,
                                          fontWeight: 800,
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                        }}
                                    >
                                      {order.name || "-"}
                                    </Typography>
                                    <Typography
                                        sx={{
                                          flexShrink: 0,
                                          color: "#202124",
                                          fontSize: 18,
                                          fontWeight: 800,
                                        }}
                                    >
                                      ${order.order_total ?? "-"}
                                    </Typography>
                                  </Box>
                                  <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.75,
                                        fontSize: 13,
                                        fontWeight: 700,
                                        color: secondaryText,
                                        minWidth: 0,
                                      }}
                                  >
                                    <Box component="span" sx={{ color: statusColor, flexShrink: 0 }}>
                                      {order.status_name || "-"}
                                    </Box>
                                    <Box component="span" sx={{ color: dividerText }}>·</Box>
                                    <Box
                                        component="span"
                                        sx={{
                                          color: paymentColor,
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                          minWidth: 0,
                                        }}
                                    >
                                      {order.pay_method || "-"}
                                    </Box>
                                    <Box component="span" sx={{ color: dividerText }}>·</Box>
                                    <Box component="span" sx={{ flexShrink: 0 }}>{order.order_time || "-"}</Box>
                                  </Box>
                                  {order.is_blank ? (
                                      <IconButton
                                          aria-label="刪除空白訂單"
                                          title="刪除空白訂單"
                                          size="small"
                                          sx={{
                                            position: "absolute",
                                            right: 8,
                                            top: "50%",
                                            transform: "translateY(-50%)",
                                            backgroundColor: "#FFF",
                                            color: "#9D1E20",
                                            "&:hover": {
                                              backgroundColor: "#F5DADA",
                                            },
                                          }}
                                          onClick={(event) => handleDeleteBlankOrder(event, order.order_id)}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                  ) : null}
                                </Box>
                            );
                        })
                        : (
                          <Box
                            sx={{
                              minHeight: 240,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "#536073",
                              fontWeight: 700,
                            }}
                          >
                            {todayOrders === null ? "載入中" : "尚未有新訂單"}
                          </Box>
                        )}
                  </Grid>
                </Box>
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                height: "100%",
                width: "100%",
                overflow: "hidden",
                backgroundColor: "#FFFBEB",
              }}
              className="app-workspace"
            >
              <Outlet />
            </Box>
          )}
        </Box>
      </Grid>
    </Grid>
  ) : (
    <SystemLoginPage
        checkAuthentication={checkAuthentication}
      setSystemLogin={setSystemLogin}
      setSystemData={setSystemData}
    />
  );
}

// 今日預約資料放在 MainMenu 外層共用：首頁卡片、預約管理徽章、全域提醒都吃同一份輪詢
function MainMenu() {
  const { systemData } = useGlobalOrder();
  const reservationsEnabled = Boolean(systemData) && systemData.client_mode !== "kiosk";
  return (
    <TodayReservationsProvider enabled={reservationsEnabled}>
      <MainMenuContent />
    </TodayReservationsProvider>
  );
}

export default MainMenu;
